import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface StarRewardOverlayProps {
  starStates?: Array<'pending' | 'success' | 'fail'>;
  highlightIndex?: number | null;
}

const STAR_SOUND_URL = 'https://syllabus-cdn.pengi.ai/assets/audio/coin.wav';
const WRONG_SOUND_URL = 'https://syllabus-cdn.pengi.ai/assets/audio/wrong.mp3';

export function StarRewardOverlay({ starStates, highlightIndex }: StarRewardOverlayProps) {
  const [starPulseIndex, setStarPulseIndex] = useState<number | null>(null);
  const [celebrationTick, setCelebrationTick] = useState(0);
  const [missTick, setMissTick] = useState(0);
  const [mounted, setMounted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wrongAudioRef = useRef<HTMLAudioElement | null>(null);
  const prevStarStatesRef = useRef<Array<'pending' | 'success' | 'fail'> | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const playStarSound = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!audioRef.current) {
        const audio = new Audio(STAR_SOUND_URL);
        audio.preload = 'auto';
        audioRef.current = audio;
      }
      audioRef.current.currentTime = 0;
      void audioRef.current.play();
    } catch {
      // Ignore audio errors (autoplay restrictions)
    }
  }, []);

  const playWrongSound = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!wrongAudioRef.current) {
        const audio = new Audio(WRONG_SOUND_URL);
        audio.preload = 'auto';
        wrongAudioRef.current = audio;
      }
      wrongAudioRef.current.currentTime = 0;
      void wrongAudioRef.current.play();
    } catch {
      // Ignore audio errors (autoplay restrictions)
    }
  }, []);

  useEffect(() => {
    if (!starStates || starStates.length === 0) {
      prevStarStatesRef.current = starStates ?? null;
      return;
    }
    const prev = prevStarStatesRef.current;
    prevStarStatesRef.current = starStates;
    if (!prev) return;
    const firstNewFailIndex = starStates.findIndex((state, idx) => state === 'fail' && prev[idx] !== 'fail');
    if (firstNewFailIndex !== -1) {
      playWrongSound();
      setMissTick((prevTick) => prevTick + 1);
    }
    const firstNewSuccessIndex = starStates.findIndex((state, idx) => state === 'success' && prev[idx] !== 'success');
    if (firstNewSuccessIndex !== -1) {
      setStarPulseIndex(firstNewSuccessIndex + 1);
      setCelebrationTick((prevTick) => prevTick + 1);
      playStarSound();
      const timer = setTimeout(() => setStarPulseIndex(null), 800);
      return () => clearTimeout(timer);
    }
  }, [starStates, playWrongSound, playStarSound]);

  const celebration =
    celebrationTick > 0 ? (
      <div key={celebrationTick} className="star-celebration" aria-hidden="true">
        <div className="star-celebration__flash" />
        <div className="star-celebration__burst star-celebration__burst--left" />
        <div className="star-celebration__burst star-celebration__burst--right" />
        <div className="star-celebration__ring" />
        <div className="star-celebration__fly-star">★</div>
      </div>
    ) : null;

  const missOverlay =
    missTick > 0 ? (
      <div key={`miss-${missTick}`} className="star-miss" aria-hidden="true">
        <div className="star-miss__pulse" />
        <div className="star-miss__star">★</div>
      </div>
    ) : null;

  return (
    <>
      {mounted && celebration ? createPortal(celebration, document.body) : null}
      {mounted && missOverlay ? createPortal(missOverlay, document.body) : null}
      <div className="sticky bottom-0 z-10 h-0 w-full overflow-visible pointer-events-none">
        <div className="flex justify-end pr-2 -translate-y-full pb-2">
          <div className="flex items-center gap-1 rounded-full border border-yellow-300/40 bg-slate-900/30 px-2 py-1 backdrop-blur-sm">
            {(starStates ?? []).map((state, idx) => {
              const isSuccess = state === 'success';
              const isFail = state === 'fail';
              const isPending = state === 'pending';
              const isPulse = starPulseIndex === idx + 1;
              return (
                <span
                  key={`star-${idx}`}
                  className={[
                    'star',
                    isSuccess ? 'star--active' : '',
                    isFail ? 'star--fail' : '',
                    isPending ? 'star--pending' : '',
                    isPulse ? 'star--pulse' : '',
                  ].join(' ')}
                  aria-label={`star ${idx + 1}`}
                >
                  ★
                </span>
              );
            })}
          </div>
        </div>
      </div>
      {typeof highlightIndex === 'number' && highlightIndex >= 0 && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
          {(() => {
            const state = starStates?.[highlightIndex] ?? 'pending';
            const isSuccess = state === 'success';
            const isFail = state === 'fail';
            const isPending = state === 'pending';
            const isPulse = starPulseIndex === highlightIndex + 1;
            return (
              <span
                className={[
                  'star',
                  'star--center',
                  isSuccess ? 'star--active' : '',
                  isFail ? 'star--fail' : '',
                  isPending ? 'star--pending' : '',
                  isPulse ? 'star--pulse' : '',
                  isPending ? 'star--highlight' : '',
                ].join(' ')}
                aria-label="reward star"
              >
                ★
              </span>
            );
          })()}
        </div>
      )}
      <style jsx>{`
        .star {
          font-size: 20px;
          line-height: 1;
          color: #e2e8f0;
          opacity: 0.3;
          text-shadow: none;
          filter: drop-shadow(0 0 0 rgba(0, 0, 0, 0));
          transition: color 200ms ease, transform 200ms ease, opacity 200ms ease;
        }
        .star--active {
          color: #fbbf24;
          opacity: 1;
          text-shadow:
            0 2px 6px rgba(0, 0, 0, 0.55),
            0 0 8px rgba(251, 191, 36, 0.8),
            0 0 16px rgba(251, 191, 36, 0.5);
        }
        .star--pending {
          opacity: 0.9;
          color: #e2e8f0;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
        }
        .star--fail {
          color: #94a3b8;
          opacity: 0.6;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
        }
        .star--center {
          font-size: 56px;
        }
        .star--highlight {
          animation: star-hint 1200ms ease-in-out infinite;
        }
        .star--pulse {
          animation: star-pop 800ms ease-out;
        }
        @keyframes star-hint {
          0% {
            transform: scale(1);
            filter: drop-shadow(0 0 0 rgba(251, 191, 36, 0));
          }
          50% {
            transform: scale(1.15);
            filter: drop-shadow(0 0 10px rgba(251, 191, 36, 0.7));
          }
          100% {
            transform: scale(1);
            filter: drop-shadow(0 0 0 rgba(251, 191, 36, 0));
          }
        }
        @keyframes star-pop {
          0% {
            transform: scale(0.6) rotate(-8deg);
            filter: drop-shadow(0 0 0 rgba(251, 191, 36, 0));
          }
          45% {
            transform: scale(1.4) rotate(6deg);
            filter: drop-shadow(0 0 18px rgba(251, 191, 36, 0.9));
          }
          100% {
            transform: scale(1) rotate(0deg);
            filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.6));
          }
        }
      `}</style>
      <style jsx global>{`
        .star-celebration {
          position: fixed;
          inset: 0;
          z-index: 1000;
          pointer-events: none;
        }
        .star-celebration__flash {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 50% 40%, rgba(255, 245, 157, 0.9), rgba(255, 255, 255, 0) 60%),
            radial-gradient(circle at 50% 60%, rgba(253, 224, 71, 0.7), rgba(255, 255, 255, 0) 70%);
          opacity: 0;
          animation: star-flash 900ms ease-out;
        }
        .star-celebration__burst {
          position: absolute;
          width: 220px;
          height: 220px;
          border-radius: 999px;
          background:
            conic-gradient(
              from 0deg,
              rgba(251, 191, 36, 0.9),
              rgba(251, 191, 36, 0.1),
              rgba(251, 191, 36, 0.9),
              rgba(251, 191, 36, 0.1),
              rgba(251, 191, 36, 0.9)
            );
          filter: blur(2px);
          opacity: 0;
          animation: star-burst 900ms ease-out;
        }
        .star-celebration__burst--left {
          top: 20%;
          left: 15%;
        }
        .star-celebration__burst--right {
          top: 20%;
          right: 15%;
        }
        .star-celebration__ring {
          position: absolute;
          left: 50%;
          top: 35%;
          width: 280px;
          height: 280px;
          transform: translateX(-50%);
          border-radius: 999px;
          border: 4px solid rgba(253, 224, 71, 0.85);
          box-shadow:
            0 0 30px rgba(253, 224, 71, 0.6),
            0 0 60px rgba(253, 224, 71, 0.4);
          opacity: 0;
          animation: star-ring 900ms ease-out;
        }
        .star-celebration__fly-star {
          position: absolute;
          left: 50%;
          top: 45%;
          transform: translate(-50%, -50%);
          font-size: 54px;
          color: #fbbf24;
          text-shadow:
            0 0 12px rgba(251, 191, 36, 0.9),
            0 0 24px rgba(251, 191, 36, 0.6);
          opacity: 0;
          animation: star-fly 1000ms ease-out;
        }
        @keyframes star-flash {
          0% { opacity: 0; }
          20% { opacity: 0.9; }
          100% { opacity: 0; }
        }
        @keyframes star-burst {
          0% { opacity: 0; transform: scale(0.4) rotate(0deg); }
          30% { opacity: 0.9; }
          100% { opacity: 0; transform: scale(1.2) rotate(90deg); }
        }
        @keyframes star-ring {
          0% { opacity: 0; transform: translateX(-50%) scale(0.4); }
          40% { opacity: 0.9; }
          100% { opacity: 0; transform: translateX(-50%) scale(1.3); }
        }
        @keyframes star-fly {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.4) rotate(-12deg);
          }
          25% { opacity: 1; }
          100% {
            opacity: 0;
            transform: translate(-50%, -200%) scale(1.2) rotate(18deg);
          }
        }
        .star-miss {
          position: fixed;
          inset: 0;
          z-index: 1000;
          pointer-events: none;
        }
        .star-miss__pulse {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 180px;
          height: 180px;
          transform: translate(-50%, -50%);
          border-radius: 999px;
          border: 3px solid rgba(148, 163, 184, 0.6);
          box-shadow: 0 0 20px rgba(148, 163, 184, 0.35);
          opacity: 0;
          animation: star-miss-pulse 700ms ease-out;
        }
        .star-miss__star {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 48px;
          color: #94a3b8;
          text-shadow: 0 2px 10px rgba(15, 23, 42, 0.4);
          opacity: 0;
          animation: star-miss-fall 700ms ease-out;
        }
        @keyframes star-miss-pulse {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
          40% { opacity: 0.9; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.2); }
        }
        @keyframes star-miss-fall {
          0% { opacity: 0; transform: translate(-50%, -80%) scale(0.6) rotate(-6deg); }
          40% { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, -10%) scale(0.9) rotate(6deg); }
        }
      `}</style>
    </>
  );
}

export default StarRewardOverlay;
